import { CurrencyValue, FieldValue, HookedRecordResult } from '@ingestro/importer-react'

import { Metadata } from 'modules/universal_importer/components/NuvoImporter/types'

import { type CompatibleColumns, getFieldValue } from './helpers'

export type TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => { value: string }

const concatenate: TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  _columns: CompatibleColumns,
  _currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  // Extract parameters
  const separator = parameters?.find((p) => p.key === 'separator')?.value || ' > '
  const columnsParam = parameters?.find((p) => p.key === 'columns')?.value || ''
  const columns = columnsParam.split(',')

  // Build the concatenated value
  const values = columns
    .map((col) => String(getFieldValue(row, col) || ''))
    .filter((val) => val.length > 0)

  const value = values.join(separator)

  return { value }
}

const slice: TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  _columns: CompatibleColumns,
  _currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => {
  // Extract parameters
  const separator = parameters?.find((p) => p.key === 'separator')?.value || ' > '
  const column = parameters?.find((p) => p.key === 'column')?.value || ''
  const start = parseInt(parameters?.find((p) => p.key === 'start')?.value || '0')
  const end = parseInt(parameters?.find((p) => p.key === 'end')?.value || '-1')

  const columnValue = String(getFieldValue(row, column) || '')
  const value = columnValue.split(separator).slice(start, end).join(separator)

  return { value }
}

const legal_entity_metadata: TransformationFunction = (
  _row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  _columns: CompatibleColumns,
  currentColumnKey: string,
  _parameters?: { key: string; value: string }[] | null
) => {
  if (currentColumnKey === 'legal_entity_country') {
    const value = String(metadata.legal_entity_countries?.[0] || '')
    return { value }
  }

  // Fallback: return first LE id from metadata (legacy hidden-field path)
  const value = String(metadata.legal_entity_ids?.[0] || '')
  return { value }
}

const total_cost_center_dedication: TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  _metadata: Metadata,
  _columns: CompatibleColumns,
  _currentColumnKey: string,
  _parameters?: { key: string; value: string }[] | null
) => {
  const costCenterFields = Object.keys(row).filter((key) => key.includes('cost_center_value_'))

  const total = costCenterFields.reduce((sum, fieldName) => {
    const fieldVal = getFieldValue(row, fieldName)
    if (fieldVal) {
      return sum + parseFloat(String(fieldVal))
    }
    return sum
  }, 0)

  const value = String(total)
  return { value }
}

// Object containing all transformation functions
export const transformationFunctions: Record<string, TransformationFunction> = {
  concatenate,
  slice,
  legal_entity_metadata,
  total_cost_center_dedication,
  // Add more transformation functions as needed: split, join ...
}
